import React, { useCallback, useEffect, useState } from "react";
import { Link, useHistory } from "react-router-dom";
import axios from "axios";
import SessionManager from "./Utils/SessionManager";
import myToast from "./Utils/MyToast";
import COLORS from "./resources/colors/COLORS";

const ACCENT = COLORS.primary_color_shade6;
const ACCENT_DARK = COLORS.primary_color_shade8;
const ACCENT_SOFT = COLORS.primary_color_shade0;

const INITIAL_FORM = {
  school_name: "",
  school_address: "",
  country: "India",
  state_id: "",
  state: "",
  district_id: "",
  district_name: "",
  admin_name: "",
  admin_phone: "",
  referal_code: "",
};

const API_BASE = () =>
  process.env.REACT_APP_SERVER_NAME + process.env.REACT_APP_TARGET_DIR;

//Created By Cursor
function resolveReferralFromQuery() {
  const search = new URLSearchParams(window.location.search);
  return (
    search.get("referral_code") ||
    search.get("referal_code") ||
    ""
  ).trim();
}

//Created By Cursor
function Register() {
  const history = useHistory();
  const [form, setForm] = useState(INITIAL_FORM);
  const [states, setStates] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [loadingStates, setLoadingStates] = useState(false);
  const [loadingDistricts, setLoadingDistricts] = useState(false);
  const [loading, setLoading] = useState(false);
  //Created By Cursor — API validation banner (parent / active school)
  const [blockNotice, setBlockNotice] = useState(null);

  useEffect(() => {
    const id = "eduok-login-fonts";
    if (document.getElementById(id)) return;
    const link = document.createElement("link");
    link.id = id;
    link.rel = "stylesheet";
    link.href =
      "https://fonts.googleapis.com/css2?family=Outfit:wght@500;600;700;800&family=Source+Sans+3:wght@400;500;600;700&display=swap";
    document.head.appendChild(link);
  }, []);

  useEffect(() => {
    const referralCode = resolveReferralFromQuery();
    if (!referralCode) return;
    setForm((prev) =>
      prev.referal_code ? prev : { ...prev, referal_code: referralCode }
    );
  }, []);

  //Created By Cursor
  const loadStates = useCallback(async () => {
    setLoadingStates(true);
    try {
      const response = await axios.get(API_BASE() + "getStateMaster.php?");
      if (response?.data?.api_status === "OK" && Array.isArray(response.data.data)) {
        setStates(response.data.data);
      } else {
        setStates([]);
        myToast.notify_warning(
          response?.data?.api_remark || "Could not load states"
        );
      }
    } catch (error) {
      console.error("loadStates error:", error);
      setStates([]);
      myToast.notify_warning("Failed to load states. Please try again.");
    } finally {
      setLoadingStates(false);
    }
  }, []);

  //Created By Cursor
  const loadDistricts = useCallback(async (stateId) => {
    if (!stateId) {
      setDistricts([]);
      return;
    }
    setLoadingDistricts(true);
    try {
      const response = await axios.get(API_BASE() + "getDistrictByState.php?", {
        params: { state_id: stateId },
      });
      if (response?.data?.api_status === "OK" && Array.isArray(response.data.data)) {
        setDistricts(response.data.data);
      } else {
        setDistricts([]);
      }
    } catch (error) {
      console.error("loadDistricts error:", error);
      setDistricts([]);
      myToast.notify_warning("Failed to load districts. Please try again.");
    } finally {
      setLoadingDistricts(false);
    }
  }, []);

  useEffect(() => {
    loadStates();
  }, [loadStates]);

  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  }, []);

  //Created By Cursor
  const handleStateChange = useCallback(
    (e) => {
      const stateId = e.target.value;
      const selected = states.find(
        (s) => String(s.state_id) === String(stateId)
      );
      setForm((prev) => ({
        ...prev,
        state_id: stateId,
        state: selected?.state_name || "",
        district_id: "",
        district_name: "",
      }));
      setDistricts([]);
      loadDistricts(stateId);
    },
    [states, loadDistricts]
  );

  //Created By Cursor
  const handleDistrictChange = useCallback(
    (e) => {
      const districtId = e.target.value;
      const selected = districts.find(
        (d) => String(d.district_id) === String(districtId)
      );
      setForm((prev) => ({
        ...prev,
        district_id: districtId,
        district_name: selected?.district_name || "",
      }));
    },
    [districts]
  );

  const validate = useCallback(() => {
    if (!form.school_name.trim()) {
      myToast.notify_warning("Please enter school name");
      return false;
    }
    if (!form.school_address.trim()) {
      myToast.notify_warning("Please enter school address");
      return false;
    }
    if (!form.country.trim()) {
      myToast.notify_warning("Please enter country");
      return false;
    }
    if (!form.state_id) {
      myToast.notify_warning("Please select state");
      return false;
    }
    if (!form.district_id) {
      myToast.notify_warning("Please select district");
      return false;
    }
    if (!form.admin_name.trim()) {
      myToast.notify_warning("Please enter admin name");
      return false;
    }
    if (!form.admin_phone.trim()) {
      myToast.notify_warning("Please enter admin phone");
      return false;
    }
    if (!/^\d{10}$/.test(form.admin_phone.trim())) {
      myToast.notify_warning("Admin phone must be a 10-digit number");
      return false;
    }
    return true;
  }, [form]);

  //Created By Cursor
  const handleSubmit = useCallback(
    async (e) => {
      if (e) e.preventDefault();
      if (!validate()) return;

      const selectedState = states.find(
        (s) => String(s.state_id) === String(form.state_id)
      );
      const selectedDistrict = districts.find(
        (d) => String(d.district_id) === String(form.district_id)
      );
      const stateName = form.state.trim() || selectedState?.state_name || "";
      const districtName =
        form.district_name.trim() || selectedDistrict?.district_name || "";

      if (!stateName) {
        myToast.notify_warning("Please select a valid state");
        return;
      }
      if (!districtName) {
        myToast.notify_warning("Please select a valid district");
        return;
      }

      setLoading(true);
      setBlockNotice(null);
      try {
        const referralToSend = form.referal_code.trim();
        const params = {
          school_name: form.school_name.trim(),
          school_address: form.school_address.trim(),
          country: form.country.trim(),
          state: stateName,
          state_id: form.state_id,
          district_id: form.district_id,
          district_name: districtName,
          admin_name: form.admin_name.trim(),
          admin_phone: form.admin_phone.trim(),
          referral_code: referralToSend,
        };

        const response = await axios.get(
          API_BASE() + "registerNewSchool.php?",
          { params }
        );
        setLoading(false);

        if (response?.data?.api_status?.toUpperCase() === "OK") {
          const school = Array.isArray(response.data.data)
            ? response.data.data[0]
            : null;
          if (!school?.school_code) {
            myToast.notify_warning(
              "School registered but school code was missing."
            );
            return;
          }

          SessionManager.saveSchoolCode(school.school_code);
          myToast.notify_success(
            response.data.api_remark || "School registered successfully!"
          );
          history.push({
            pathname: "/SessionSetting",
            state: {
              school_code: school.school_code,
              school_name: school.school_name,
            },
          });
        } else {
          //Created By Cursor — clear UI for parent / active school (Hindi + school details)
          const errorCode = String(response?.data?.error_code || "").toUpperCase();
          const remark =
            response?.data?.api_remark ||
            "Registration failed. Please try again.";
          if (
            errorCode === "PARENT_CONTACT_EXISTS" ||
            errorCode === "SCHOOL_ALREADY_ACTIVE"
          ) {
            const payload = response?.data?.data || {};
            const title =
              errorCode === "PARENT_CONTACT_EXISTS"
                ? "Parent number cannot register a school"
                : "School already registered";
            const titleHi =
              errorCode === "PARENT_CONTACT_EXISTS"
                ? "अभिभावक नंबर से स्कूल रजिस्ट्रेशन नहीं हो सकता"
                : "स्कूल पहले से पंजीकृत है";
            setBlockNotice({
              code: errorCode,
              title,
              titleHi,
              message: remark,
              messageHi:
                response?.data?.api_remark_hi ||
                (errorCode === "PARENT_CONTACT_EXISTS"
                  ? "यह मोबाइल नंबर पहले से अभिभावक (Parent) के रूप में पंजीकृत है। अभिभावक नंबर से स्कूल रजिस्ट्रेशन की अनुमति नहीं है। कृपया पैरेंट लॉगिन से साइन इन करें।"
                  : "यह मोबाइल नंबर पहले से सक्रिय स्कूल से जुड़ा है। दोबारा रजिस्ट्रेशन की अनुमति नहीं है। कृपया साइन इन करें या EduOK Support से संपर्क करें।"),
              schoolName: payload.school_name || "",
              officialContact:
                payload.official_contact ||
                payload.officail_contact_no1 ||
                payload.officail_contact_no2 ||
                "",
              //Created By Cursor — EduOK Support (Priyesh Mourya) for school re-registration
              eduokSupportName: payload.eduok_support_name || "",
              eduokSupportPhone: payload.eduok_support_phone || "",
              eduokSupportEmail: payload.eduok_support_email || "",
              eduokSupportLabel: payload.eduok_support_label || "EduOK Support",
            });
            myToast.notify_warning(remark);
            return;
          }
          myToast.notify_warning(remark);
        }
      } catch (error) {
        setLoading(false);
        console.error("Register error:", error);
        myToast.notify_warning(
          error.response?.data?.api_remark ||
            "Network error. Please check your connection."
        );
      }
    },
    [form, states, districts, validate, history]
  );

  const inputBase = {
    width: "100%",
    height: 46,
    padding: "0 12px",
    borderRadius: 10,
    border: "1.5px solid #d0d7e2",
    background: "#fff",
    fontFamily: '"Source Sans 3", sans-serif',
    fontSize: 14,
    color: "#1e293b",
    outline: "none",
    boxSizing: "border-box",
  };

  const labelStyle = {
    display: "block",
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    color: "#64748b",
    marginBottom: 6,
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        fontFamily: '"Source Sans 3", sans-serif',
        background: `linear-gradient(145deg, ${ACCENT_SOFT} 0%, #ffffff 42%, #e0f2fe 100%)`,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "32px 16px",
        boxSizing: "border-box",
      }}
    >
      <style>{`
        .eduok-reg-input:focus {
          border-color: ${ACCENT} !important;
          box-shadow: 0 0 0 3px ${ACCENT_SOFT} !important;
        }
        .eduok-reg-btn:hover:not(:disabled) {
          background: ${ACCENT_DARK} !important;
          transform: translateY(-1px);
        }
      `}</style>

      <div
        style={{
          width: 520,
          maxWidth: "100%",
          padding: "32px 30px 28px",
          borderRadius: 18,
          background: "rgba(255,255,255,0.96)",
          border: "1px solid #d7e6f4",
          boxShadow: "0 16px 40px rgba(1, 87, 155, 0.12)",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            marginBottom: 8,
          }}
        >
          <img
            src={`${process.env.PUBLIC_URL || ""}/images/logo.svg`}
            alt="EduOK"
            style={{ width: 44, height: 44, borderRadius: 10 }}
          />
          <div
            style={{
              fontFamily: '"Outfit", sans-serif',
              fontSize: 28,
              fontWeight: 800,
              color: ACCENT_DARK,
              letterSpacing: "-0.02em",
            }}
          >
            Edu<span style={{ color: ACCENT }}>OK</span>
          </div>
        </div>

        {!blockNotice && (
          <>
            <p
              style={{
                margin: "0 0 6px",
                fontFamily: '"Outfit", sans-serif',
                fontSize: 22,
                fontWeight: 700,
                color: "#0f172a",
              }}
            >
              Register your school
            </p>
            <p style={{ margin: "0 0 22px", fontSize: 14, color: "#64748b" }}>
              Create an admin account to start managing your campus.
            </p>
          </>
        )}

        {/* Created By Cursor — parent/school block: hide registration form */}
        {blockNotice ? (
          <>
            <div
              role="alert"
              style={{
                marginBottom: 18,
                padding: "14px 16px",
                borderRadius: 12,
                border: "1px solid #fecaca",
                background: "#fef2f2",
                color: "#7f1d1d",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 10,
                }}
              >
                <i
                  className="fa fa-exclamation-circle"
                  style={{ marginTop: 3, color: "#dc2626", fontSize: 22 }}
                  aria-hidden
                />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p
                    style={{
                      margin: "0 0 4px",
                      fontFamily: '"Outfit", sans-serif',
                      fontWeight: 700,
                      fontSize: 18,
                      color: "#991b1b",
                    }}
                  >
                    {blockNotice.title}
                  </p>
                  {blockNotice.titleHi ? (
                    <p
                      style={{
                        margin: "0 0 10px",
                        fontWeight: 700,
                        fontSize: 17,
                        color: "#b91c1c",
                      }}
                    >
                      {blockNotice.titleHi}
                    </p>
                  ) : null}
                  <p style={{ margin: "0 0 8px", fontSize: 15, lineHeight: 1.5 }}>
                    {blockNotice.message}
                  </p>
                  {blockNotice.messageHi ? (
                    <p
                      style={{
                        margin: "0 0 12px",
                        fontSize: 15,
                        lineHeight: 1.55,
                        color: "#9f1239",
                      }}
                    >
                      {blockNotice.messageHi}
                    </p>
                  ) : null}
                  {(blockNotice.schoolName ||
                    blockNotice.officialContact ||
                    blockNotice.eduokSupportPhone ||
                    blockNotice.eduokSupportEmail) && (
                    <div
                      style={{
                        marginTop: 4,
                        padding: "12px 14px",
                        borderRadius: 10,
                        background: "#fff",
                        border: "1px solid #fecaca",
                        fontSize: 15,
                        color: "#7f1d1d",
                        lineHeight: 1.5,
                      }}
                    >
                      {blockNotice.schoolName ? (
                        <p style={{ margin: "0 0 4px" }}>
                          <strong>School / स्कूल:</strong>{" "}
                          {blockNotice.schoolName}
                        </p>
                      ) : null}
                      {blockNotice.officialContact ? (
                        <p
                          style={{
                            margin:
                              blockNotice.eduokSupportPhone ||
                              blockNotice.eduokSupportEmail
                                ? "0 0 8px"
                                : 0,
                          }}
                        >
                          <strong>Official contact / आधिकारिक संपर्क:</strong>{" "}
                          {blockNotice.officialContact}
                        </p>
                      ) : null}
                      {/* Created By Cursor — EduOK Support for school re-registration */}
                      {blockNotice.code === "SCHOOL_ALREADY_ACTIVE" &&
                      (blockNotice.eduokSupportPhone ||
                        blockNotice.eduokSupportEmail) ? (
                        <div
                          style={{
                            marginTop: blockNotice.officialContact ? 0 : 4,
                            paddingTop: 8,
                            borderTop: "1px dashed #fecaca",
                          }}
                        >
                          <p style={{ margin: "0 0 4px", fontWeight: 700 }}>
                            {blockNotice.eduokSupportLabel || "EduOK Support"}
                          </p>
                          {blockNotice.eduokSupportName ? (
                            <p style={{ margin: "0 0 2px" }}>
                              {blockNotice.eduokSupportName}
                            </p>
                          ) : null}
                          {blockNotice.eduokSupportPhone ? (
                            <p style={{ margin: "0 0 2px" }}>
                              <strong>Phone:</strong>{" "}
                              <a
                                href={`tel:${blockNotice.eduokSupportPhone}`}
                                style={{ color: "#991b1b", fontWeight: 600 }}
                              >
                                {blockNotice.eduokSupportPhone}
                              </a>
                            </p>
                          ) : null}
                          {blockNotice.eduokSupportEmail ? (
                            <p style={{ margin: 0 }}>
                              <strong>Email:</strong>{" "}
                              <a
                                href={`mailto:${blockNotice.eduokSupportEmail}`}
                                style={{ color: "#991b1b", fontWeight: 600 }}
                              >
                                {blockNotice.eduokSupportEmail}
                              </a>
                            </p>
                          ) : null}
                        </div>
                      ) : null}
                    </div>
                  )}
                </div>
              </div>
            </div>
            <button
              type="button"
              className="eduok-reg-btn"
              onClick={() => history.push("/Login")}
              style={{
                width: "100%",
                height: 50,
                border: "none",
                borderRadius: 10,
                background: ACCENT,
                color: "#fff",
                fontFamily: '"Outfit", sans-serif',
                fontSize: 16,
                fontWeight: 700,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 10,
                transition: "background 0.2s ease, transform 0.2s ease",
              }}
            >
              <i className="fa fa-sign-in" aria-hidden />
              Go To Login
            </button>
          </>
        ) : (
        <form onSubmit={handleSubmit} style={{ padding: 0 }}>
          <div style={{ marginBottom: 14 }}>
            <label htmlFor="school_name" style={labelStyle}>
              School Name
            </label>
            <input
              id="school_name"
              name="school_name"
              className="eduok-reg-input"
              value={form.school_name}
              onChange={handleChange}
              placeholder="Enter school name"
              disabled={loading}
              style={inputBase}
              required
            />
          </div>

          <div style={{ marginBottom: 14 }}>
            <label htmlFor="school_address" style={labelStyle}>
              School Address
            </label>
            <input
              id="school_address"
              name="school_address"
              className="eduok-reg-input"
              value={form.school_address}
              onChange={handleChange}
              placeholder="Enter school address"
              disabled={loading}
              style={inputBase}
              required
            />
          </div>

          <div style={{ marginBottom: 14 }}>
            <label htmlFor="country" style={labelStyle}>
              Country
            </label>
            <input
              id="country"
              name="country"
              className="eduok-reg-input"
              value={form.country}
              onChange={handleChange}
              disabled={loading}
              style={inputBase}
              required
            />
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 12,
              marginBottom: 14,
            }}
          >
            <div>
              <label htmlFor="state_id" style={labelStyle}>
                State
              </label>
              <select
                id="state_id"
                name="state_id"
                className="eduok-reg-input"
                value={form.state_id}
                onChange={handleStateChange}
                disabled={loading || loadingStates}
                style={{
                  ...inputBase,
                  cursor: loadingStates ? "wait" : "pointer",
                }}
                required
              >
                <option value="">
                  {loadingStates ? "Loading states…" : "Select state"}
                </option>
                {states.map((s) => (
                  <option key={s.state_id} value={s.state_id}>
                    {s.state_name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="district_id" style={labelStyle}>
                District
              </label>
              <select
                id="district_id"
                name="district_id"
                className="eduok-reg-input"
                value={form.district_id}
                onChange={handleDistrictChange}
                disabled={loading || !form.state_id || loadingDistricts}
                style={{
                  ...inputBase,
                  cursor:
                    !form.state_id || loadingDistricts ? "not-allowed" : "pointer",
                  background: !form.state_id ? "#f8fafc" : "#fff",
                }}
                required
              >
                <option value="">
                  {!form.state_id
                    ? "Select state first"
                    : loadingDistricts
                    ? "Loading districts…"
                    : "Select district"}
                </option>
                {districts.map((d) => (
                  <option key={d.district_id} value={d.district_id}>
                    {d.district_name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 12,
              marginBottom: 14,
            }}
          >
            <div>
              <label htmlFor="admin_name" style={labelStyle}>
                Admin Name
              </label>
              <input
                id="admin_name"
                name="admin_name"
                className="eduok-reg-input"
                value={form.admin_name}
                onChange={handleChange}
                placeholder="Admin full name"
                disabled={loading}
                style={inputBase}
                required
              />
            </div>
            <div>
              <label htmlFor="admin_phone" style={labelStyle}>
                Admin Phone
              </label>
              <input
                id="admin_phone"
                name="admin_phone"
                className="eduok-reg-input"
                value={form.admin_phone}
                onChange={handleChange}
                placeholder="10-digit mobile"
                disabled={loading}
                style={inputBase}
                inputMode="numeric"
                maxLength={10}
                required
              />
            </div>
          </div>

          <div style={{ marginBottom: 20 }}>
            <label htmlFor="referal_code" style={labelStyle}>
              Referral Code (optional)
            </label>
            <input
              id="referal_code"
              name="referal_code"
              className="eduok-reg-input"
              value={form.referal_code}
              onChange={handleChange}
              placeholder="If you have one"
              disabled={loading}
              style={inputBase}
            />
          </div>

          <button
            type="submit"
            className="eduok-reg-btn"
            disabled={loading}
            style={{
              width: "100%",
              height: 50,
              border: "none",
              borderRadius: 10,
              background: ACCENT,
              color: "#fff",
              fontFamily: '"Outfit", sans-serif',
              fontSize: 16,
              fontWeight: 700,
              cursor: loading ? "not-allowed" : "pointer",
              opacity: loading ? 0.75 : 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 10,
              transition: "background 0.2s ease, transform 0.2s ease",
            }}
          >
            {loading ? (
              <>
                <i className="fa fa-spinner fa-spin" aria-hidden />
                Registering…
              </>
            ) : (
              <>
                <i className="fa fa-user-plus" aria-hidden />
                Register School
              </>
            )}
          </button>
        </form>
        )}

        {!blockNotice && (
        <p
          style={{
            margin: "20px 0 0",
            textAlign: "center",
            fontSize: 14,
            color: "#64748b",
          }}
        >
          Already have an account?{" "}
          <Link
            to="/Login"
            style={{
              color: ACCENT,
              fontWeight: 700,
              textDecoration: "none",
            }}
          >
            Sign in
          </Link>
        </p>
        )}
      </div>
    </div>
  );
}

export default Register;
